// Minimal context for the AddAi Chat sidepanel — exposes open/close so
// header buttons (or anything else) can toggle the panel without prop
// drilling. The panel itself is rendered inside the dashboard layout
// (Dashboard.jsx) so that opening it pushes the main content over instead
// of overlaying it. The open/closed state is persisted to localStorage so
// it survives reloads.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

const ChatPanelContext = createContext(null);
const STORAGE_KEY = 'addai_chat_open';

export const useChatPanel = () => {
  const ctx = useContext(ChatPanelContext);
  if (!ctx) {
    // Returning a noop instead of throwing keeps the Header useful even
    // on routes the provider doesn't wrap (e.g. /login). The button
    // wouldn't open anything there anyway.
    return { isOpen: false, open: () => {}, close: () => {}, toggle: () => {} };
  }
  return ctx;
};

export const ChatPanelProvider = ({ children }) => {
  // Restore the last open/closed state so the panel stays put across reloads.
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, isOpen ? '1' : '0');
    } catch {
      /* ignore storage failures (private mode / disabled storage) */
    }
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle]
  );

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  );
};

export default ChatPanelProvider;
